import logoCondenseImg from "../../assets/imgs/logo-condense.png";
import { CONDENSE_TRIAL } from "../../util/constants";

export default {
  id: "condense",
  appName: "Condense",
  logo: logoCondenseImg,

  labels: {
    videos: "Videos",
    myVideos: "My Videos",
    search: "Search videos...",
    content: "videos",
    contentSingular: "video",
    blog: "Media Insights",
  },

  footer: { owner: "Ring Digital LLC", email: "Brian@RingDigital.tv", stripped: false },

  theme: {
    accent: "#22c55e",
    headerTextCls: "text-green-700 dark:text-green-400",
    searchRingCls: "focus:ring-green-500",
    quoteIconCls: "text-green-600 dark:text-green-400",
    quoteTextCls: "text-green-400 dark:text-green-300",
    quoteRingCls: "ring-green-500",
    quoteBorderCls: "border-green-500 dark:border-green-400",
    navActiveCls: "bg-green-50 text-green-700 dark:bg-green-900/40 dark:text-green-400",
    navActiveDrawerCls: "bg-green-50 text-green-700 dark:bg-green-900/40 dark:text-green-400 border-r-2 border-green-600",
    chat: {
      accent: "#22c55e",
      accentShadow: "rgba(34, 197, 94, 0.4)",
      tabActiveBg: "#dcfce7",
      tabActiveText: "#16a34a",
      tabActiveBorder: "rgba(34, 197, 94, 0.35)",
    },
  },

  features: {
    blog: false,
    payments: true,
    communityDrip: true,
    shareDrip: false,
    quotePortal: true,
    combatTools: true,
    sportsScorebug: true,
    telegramChat: true,
    selfServicePlans: false,
  },

  communityDripCuratorId: "69a5cc4523fa376cfada19c6",

  seoDescription: "Media and broadcast video workflows with AI condensing.",

  marketing: {
    features: [
      { emoji: "📡", title: "Ad Cue Placement", desc: "Export Roku cuepoints JSON + SCTE-35 XML sidecars from any condensed video. Built for broadcast and CTV monetization." },
      { emoji: "🎛️", title: "Per-Asset-Type Controls", desc: "Tuned editors and AI prompts for news, baseball, football, table tennis, and combat sports. Each format gets the right interface." },
      { emoji: "📑", title: "Auto-Chaptered Videos", desc: "AI breaks long recordings into logical chapters. Perfect for news, sports, talk shows, and channel playout—skim and zero-in on highlights in minutes." },
      { emoji: "💬", title: "Magic Quote Extraction", desc: "Identify and extract the most compelling quotes for clips, social posts, and auto-condensed highlight reels." },
      { emoji: "✂️", title: "One-Click Auto-Condense", desc: "Select your best quotes and generate a condensed highlight reel. Cue-in / Cue-out flow for precise control when you need it." },
      { emoji: "🔍", title: "Searchable Library", desc: "Search across all your videos by keywords, topics, or quotes. Find any moment across your content library." },
      { emoji: "🧩", title: "Embeddable Portal", desc: "Create a custom video portal for chaptered content that works on any website or landing page." },
      { emoji: "❝", title: "Top Pull Quotes Portal", desc: "A second embeddable portal that surfaces the best pull quotes from across your library — auto-aggregated or hand-picked." },
      { emoji: "📸", title: "Screenshots", desc: "Capture screengrabs at any timestamp. Essential for presentations and documentation." },
    ],
    assetTypes: [
      {
        id: "news",
        label: "News & Talk",
        emoji: "🎙️",
        headline: "Long-form to shareable clips",
        bullets: [
          "Auto-chapter hours-long broadcasts and podcasts",
          "Pull verbatim quotes for stories and social",
          "Condense to a story-length highlight reel",
          "Top Pull Quotes portal for your site",
        ],
      },
      {
        id: "baseball",
        label: "Baseball",
        emoji: "⚾",
        headline: "Inning-aware condensing",
        bullets: [
          "Scorebug OCR detects inning, count, and score automatically",
          "AI chapters at the half-inning level",
          "Approve plays, then export an at-bat highlight reel",
          "Ad cuepoints between innings for broadcast playout",
        ],
      },
      {
        id: "football",
        label: "Football",
        emoji: "🏈",
        headline: "Drive + scoring play detection",
        bullets: [
          "Drive-aware chaptering using down/distance OCR",
          "Auto-flag scoring plays, turnovers, and big gains",
          "Game Editor for clip-by-clip approval before export",
          "Roku cuepoints + SCTE-35 sidecars for CTV",
        ],
      },
      {
        id: "tt",
        label: "Table Tennis",
        emoji: "🏓",
        headline: "Rally-by-rally clips",
        bullets: [
          "Per-rally chaptering using score detection",
          "Auto-condense to the deciding points of every game",
          "Slow-motion-friendly cue points",
          "Tournament-ready highlight reels",
        ],
      },
      {
        id: "combat",
        label: "Combat Sports",
        emoji: "🥊",
        headline: "Round + finish detection",
        bullets: [
          "Round-by-round chaptering for boxing and MMA",
          "Auto-flag knockdowns, finishes, and judge decisions",
          "Match Editor with combat-specific controls",
          "Approve clips, then export condensed bouts",
        ],
      },
    ],
    adCue: {
      title: "Ad Cue Placement for Broadcast & CTV",
      sub: "Condense doesn't just shorten your video — it prepares it for monetization on Roku, Fire TV, and linear playout.",
      bullets: [
        "Auto-detect natural ad breaks between approved clips",
        "Export Roku cuepoints JSON sidecar",
        "Export SCTE-35 XML sidecar for linear and CTV",
        "Override timing in the Game Editor before export",
      ],
      sample: [
        '{',
        '  "title": "Game Highlights",',
        '  "cuepoints": [',
        '    { "type": "preroll", "offset": 0 },',
        '    { "type": "midroll", "offset": 312 },',
        '    { "type": "midroll", "offset": 687 }',
        '  ]',
        '}',
      ].join("\n"),
      cta: { label: "Sign Up Free", href: "/register" },
    },
    personas: [
      { title: "Broadcast Producer", tag: "Condense hours of news or talk into shareable clips.", desc: "Auto-extract highlights for YouTube, social, and channel playout without manual scrubbing." },
      { title: "Sports Editor", tag: "Turn full game replays into highlight reels.", desc: "AI-powered chaptering and quote extraction. One-click auto-condense for the moments that matter." },
      { title: "Talk Show Team", tag: "Repurpose long-form into clips and shorts.", desc: "Find the best quotes, generate condensed reels, and feed multiple platforms from one upload." },
      { title: "Content Strategist", tag: "One recording, many formats.", desc: "News, sports, talk—extract clips, quotes, and condensed reels for YouTube, social, and more." },
    ],
    hero: {
      title: "Auto-Condense Long Videos for YouTube & Channel Playout",
      sub: "Built for media workflows: news, sports, talk, and TV. Upload long recordings, let AI find the best moments, and generate condensed highlight reels in minutes.",
      assetTypesLine: "News · Talk · Baseball · Football · Table Tennis · Combat",
    },
    loginVisitor: {
      title: "Auto-Condense Long Videos",
      desc: "Built for media workflows: news, sports, talk, and TV. AI-powered quote extraction, clips, and one-click condensed reels.",
    },
    chatOpeners: [
      "How can I help? To report a bug, just attach a screenshot to this chat and describe the problem.",
    ],
    chatPlaceholder: "Describe your question or bug…",
    chatIntakeOnly: true,
    sections: {
      featuresTitle: "Built for Media Workflows",
      featuresSub: "Upload any long video and unlock organized chapters with compelling quotes ready for clips and condensed reels.",
      demoTitle: "See Condense in Action",
      personasTitle: "Works Across Formats",
      personasSub: "News, sports, talk, channel playout—Condense works wherever you have long-form video.",
      finalCtaTitle: "Ready to condense your long videos?",
      finalCtaSub: `Create an account, upload one video free, and start a ${CONDENSE_TRIAL.evaluationHours}-hour evaluation when your first video is ready.`,
    },
    blogLabel: "Media Insights",
  },
};
